import { useMemo } from "react";
import { findRecipeBySlug, useMenu } from "@/features/menu/store";
import { useMenuPlan } from "@/hooks/useMenuPlan";
import { useRecipeSteps } from "@/hooks/useRecipeSteps";
import { useHouseholdProfile } from "@/features/household/useHouseholdProfile";
import { WEEKDAY_LABELS_LONG } from "@/features/onboarding/constants";
import type { CookingStep, MenuPlan, Recipe } from "@/types/menu";

export type CookingMode = "batch" | "recipe";

/**
 * Unified session interface — same shape regardless of mode, so the
 * Cooking Mode component renders identically against either source.
 *
 *   mode="batch":  reads plan.cooking_plan + cookingIndex/doneSteps
 *   mode="recipe": reads recipe.prep_steps + recipeProgress[slug]
 */
export interface CookingSession {
  mode: CookingMode;
  plan: MenuPlan | null;
  isLoadingPlan: boolean;
  isLoadingSteps: boolean;
  stepsError: string | null;

  steps: CookingStep[];
  index: number;
  step: CookingStep | undefined;
  doneOrders: number[];

  /** Active recipe (recipe mode) or null (batch mode). */
  recipe: Recipe | null;
  /** Title displayed in the header. Falls back to "Batch dimanche". */
  title: string;

  next: () => void;
  prev: () => void;
  goTo: (i: number) => void;
  markDone: (order: number) => void;
  markUndone: (order: number) => void;
  reset: () => void;
}

export function useCookingSession(
  mode: CookingMode,
  recipeSlug: string | null,
): CookingSession {
  const { plan, isLoading: isLoadingPlan } = useMenuPlan();
  const { profile } = useHouseholdProfile();
  const cookingDayLabel =
    WEEKDAY_LABELS_LONG[profile?.cooking_day ?? 6].toLowerCase();

  // Both stores are read regardless of mode, so we can switch on the fly.
  const cookingIndex = useMenu((s) => s.cookingIndex);
  const doneSteps = useMenu((s) => s.doneSteps);
  const recipeProgress = useMenu((s) => s.recipeProgress);

  const nextStep = useMenu((s) => s.nextStep);
  const prevStep = useMenu((s) => s.prevStep);
  const goToStep = useMenu((s) => s.goToStep);
  const markStepDone = useMenu((s) => s.markStepDone);
  const markStepUndone = useMenu((s) => s.markStepUndone);

  const nextRecipeStep = useMenu((s) => s.nextRecipeStep);
  const prevRecipeStep = useMenu((s) => s.prevRecipeStep);
  const goToRecipeStep = useMenu((s) => s.goToRecipeStep);
  const markRecipeStepDone = useMenu((s) => s.markRecipeStepDone);
  const markRecipeStepUndone = useMenu((s) => s.markRecipeStepUndone);

  const recipe: Recipe | null =
    mode === "recipe" && recipeSlug ? findRecipeBySlug(plan, recipeSlug) : null;

  // Lazy fetch + cache prep_steps for the active recipe.
  const stepsState = useRecipeSteps(mode === "recipe" ? recipe : null, plan);

  return useMemo<CookingSession>(() => {
    if (mode === "recipe" && recipe) {
      const progress = recipeProgress[recipe.slug] ?? { index: 0, done: [] };
      const steps = recipe.prep_steps;
      return {
        mode,
        plan,
        isLoadingPlan,
        isLoadingSteps: stepsState.isLoading,
        stepsError: stepsState.error,
        steps,
        index: progress.index,
        step: steps[progress.index],
        doneOrders: progress.done,
        recipe,
        title: recipe.title,
        next: () => nextRecipeStep(recipe.slug),
        prev: () => prevRecipeStep(recipe.slug),
        goTo: (i) => goToRecipeStep(recipe.slug, i),
        markDone: (o) => markRecipeStepDone(recipe.slug, o),
        markUndone: (o) => markRecipeStepUndone(recipe.slug, o),
        reset: () => goToRecipeStep(recipe.slug, 0),
      };
    }

    // Batch mode (default).
    const steps = plan?.cooking_plan ?? [];
    return {
      mode: "batch",
      plan,
      isLoadingPlan,
      isLoadingSteps: false,
      stepsError: null,
      steps,
      index: cookingIndex,
      step: steps[cookingIndex],
      doneOrders: doneSteps,
      recipe: null,
      title: `Batch ${cookingDayLabel}`,
      next: nextStep,
      prev: prevStep,
      goTo: goToStep,
      markDone: markStepDone,
      markUndone: markStepUndone,
      reset: () => goToStep(0),
    };
  }, [
    mode,
    recipe,
    plan,
    isLoadingPlan,
    stepsState.isLoading,
    stepsState.error,
    cookingIndex,
    doneSteps,
    recipeProgress,
    cookingDayLabel,
    nextStep,
    prevStep,
    goToStep,
    markStepDone,
    markStepUndone,
    nextRecipeStep,
    prevRecipeStep,
    goToRecipeStep,
    markRecipeStepDone,
    markRecipeStepUndone,
  ]);
}
